import React, { useEffect, useMemo, useState } from "react";
import { tbkVenta, tbkSoportado } from "@/lib/tbkPos";
import { useNavigate } from "react-router-dom";
import { normalizeTiers, tierPrice } from "@/lib/pricing";
import { api, clp, resolveMedia, MAX_LINEAS_DTE, AVISO_LIMITE_LINEAS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { computePromotions } from "@/lib/promotions";
import { formatRut, validateRut, normalizeRut } from "@/lib/rut";
import { BRAND } from "@/config/brand";
import { ArrowLeft, Plus, Minus, ShoppingCart, Check, CreditCard, Trash2, Maximize2, Lock, User, Search } from "lucide-react";

export default function Kiosk() {
  const nav = useNavigate();
  const { store, company } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loyaltyCfg, setLoyaltyCfg] = useState(null);
  const [cat, setCat] = useState("ALL");
  const [cart, setCart] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [step, setStep] = useState("welcome"); // welcome | shop
  const [fullscreen, setFullscreen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [custRut, setCustRut] = useState("");
  const [custName, setCustName] = useState("");
  const [custInfo, setCustInfo] = useState(null);
  const [custLoading, setCustLoading] = useState(false);

  // Autoservicio: se carga por familia desde el servidor (catálogos de 25.000 SKU)
  useEffect(() => {
    api.get("/products", {
      params: { limit: 200, estado: "ACTIVOS", canal: "AUTOSERVICIO", scope: "branch",
                category_id: cat === "ALL" ? undefined : cat },
    }).then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, [cat]);

  useEffect(() => {
    (async () => {
      const [c, pr, lc] = await Promise.all([
        api.get("/categories"), api.get("/promotions?canal=AUTOSERVICIO"),
        api.get("/loyalty/config", { silent402: true }).catch(() => ({ data: null })),
      ]);
      setCategories(c.data);
      setPromotions(pr.data);
      setLoyaltyCfg(lc.data);
    })();
  }, []);

  const goFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreen(true);
    } catch (_) {}
    setStep("shop");
  };

  const filtered = products;

  const add = (p) => {
    if ((p.stock || 0) <= 0) return;
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.product_id);
      if (ex) {
        if (ex.cantidad + 1 > p.stock) return c;
        return c.map((x) => x.product_id === p.product_id
          ? { ...x, cantidad: x.cantidad + 1, precio: tierPrice(x.precio_base ?? x.precio, x.tiers, x.cantidad + 1) }
          : x);
      }
      if (c.length >= MAX_LINEAS_DTE) {
        toast.error(AVISO_LIMITE_LINEAS, { id: "limite-lineas", duration: 8000 });
        return c;
      }
      const tiers = normalizeTiers(p.precios_cantidad);
      return [...c, { product_id: p.product_id, nombre: p.nombre, precio: tierPrice(p.precio, tiers, 1), precio_base: p.precio, tiers, cantidad: 1, stock: p.stock, image_url: p.image_url }];
    });
  };
  const inc = (id, d) => setCart((c) => c.map((x) => {
    if (x.product_id !== id) return x;
    const nueva = Math.max(1, x.cantidad + d);
    if (nueva > (x.stock || 0)) return x;
    return { ...x, cantidad: nueva, precio: tierPrice(x.precio_base ?? x.precio, x.tiers, nueva) };
  }));
  const rm = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const rawSubtotal = cart.reduce((s, x) => s + x.precio * x.cantidad, 0);
  const promoResult = useMemo(() => computePromotions(cart, promotions, "canal_autoservicio"), [cart, promotions]);
  const total = Math.max(0, rawSubtotal - promoResult.savings);

  const loyaltyActive = !!(loyaltyCfg?.enabled && loyaltyCfg?.canal_autoservicio);

  const lookupCustomer = async () => {
    const norm = normalizeRut(custRut);
    if (!norm) { toast.error("RUT inválido"); return; }
    setCustLoading(true);
    try {
      const r = await api.get(`/loyalty/customer_by_rut?rut=${encodeURIComponent(norm)}`);
      setCustInfo(r.data);
      if (r.data?.found && r.data?.name && !custName) setCustName(r.data.name);
      if (!r.data?.found) toast.info("Cliente nuevo · se registrará al pagar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setCustLoading(false); }
  };

  const resetCustomer = () => { setCustRut(""); setCustName(""); setCustInfo(null); };

  const [tbkEstado, setTbkEstado] = useState(null);
  const pay = async () => {
    let normRut = null;
    if (loyaltyActive && custRut) {
      normRut = normalizeRut(custRut);
      if (!normRut) { toast.error("RUT inválido"); return; }
      if (!custName.trim()) { toast.error("Ingresa el nombre del cliente"); return; }
    }
    // Autoservicio con POS Transbank integrado en este equipo: se cobra antes de registrar la venta
    let tbk = null;
    if (localStorage.getItem("aipos_tbk") === "1" && tbkSoportado()) {
      setTbkEstado("Acerca, inserta o desliza tu tarjeta en el POS");
      try {
        tbk = await tbkVenta(total, String(Date.now()).slice(-6), setTbkEstado);
        if (!tbk.aprobado) { toast.error(`Pago rechazado: ${tbk.mensaje}`); return; }
      } catch (e) { toast.error(e?.message || "No se pudo cobrar con la tarjeta"); return; }
      finally { setTbkEstado(null); }
    }
    try {
      const r = await api.post("/sales", {
        store_id: store.store_id,
        items: cart.map((x) => ({ product_id: x.product_id, cantidad: x.cantidad })),
        payment_method: "TARJETA",
        canal: "AUTOSERVICIO",
        ...(tbk ? { tbk } : {}),
        ...(normRut ? { customer_rut: normRut, customer_name: custName.trim() } : {}),
      });
      setConfirm(r.data);
      setCart([]);
      setPayOpen(false);
      resetCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const backToWelcome = () => {
    setStep("welcome");
    setCart([]);
    setConfirm(null);
  };

  const attemptExit = () => {
    setPin(""); setPinError(false); setPinOpen(true);
  };

  const verifyPin = async () => {
    try {
      await api.post("/kiosk/pin/verify", { pin });
      setPinOpen(false);
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (_) {}
      nav("/admin");
    } catch (e) {
      setPinError(true);
      setPin("");
    }
  };

  const onPinKey = (e) => {
    if (e.key === "Enter" && pin.length >= 4) verifyPin();
  };

  // WELCOME SCREEN
  const pinDialog = (
    <Dialog open={pinOpen} onOpenChange={(v) => { setPinOpen(v); if (!v) { setPin(""); setPinError(false); } }}>
      <DialogContent className="rounded-3xl max-w-sm">
        <DialogHeader><DialogTitle className="text-center text-xl">Ingresa el PIN de administrador</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-slate-100 text-slate-500 grid place-items-center">
            <Lock className="w-7 h-7" />
          </div>
          <div className="flex justify-center gap-2" data-testid="kiosk-pin-dots">
            {[0,1,2,3,4,5,6,7].slice(0, Math.max(4, pin.length || 4)).map((i) => (
              <div key={i} className={`w-3 h-3 rounded-full ${i < pin.length ? (pinError ? "bg-rose-500" : "bg-sky-500") : "bg-slate-200"}`} />
            ))}
          </div>
          {pinError && <p className="text-sm text-rose-600 text-center font-semibold">PIN incorrecto</p>}
          <div className="grid grid-cols-3 gap-2">
            {["1","2","3","4","5","6","7","8","9"].map((d) => (
              <button key={d} data-testid={`kiosk-pin-${d}`} onClick={() => { setPinError(false); setPin((p) => (p.length < 8 ? p + d : p)); }}
                className="h-14 rounded-2xl bg-white border border-slate-200 text-2xl font-mono-num font-bold text-slate-800 hover:bg-sky-50 active:scale-95 transition">{d}</button>
            ))}
            <button onClick={() => { setPin(""); setPinError(false); }} className="h-14 rounded-2xl bg-white border border-slate-200 text-sm text-slate-500 hover:bg-slate-50">Limpiar</button>
            <button data-testid="kiosk-pin-0" onClick={() => { setPinError(false); setPin((p) => (p.length < 8 ? p + "0" : p)); }}
              className="h-14 rounded-2xl bg-white border border-slate-200 text-2xl font-mono-num font-bold text-slate-800 hover:bg-sky-50 active:scale-95 transition">0</button>
            <button onClick={() => setPin((p) => p.slice(0, -1))} className="h-14 rounded-2xl bg-white border border-slate-200 text-sm text-slate-500 hover:bg-slate-50">←</button>
          </div>
          <Button data-testid="kiosk-pin-submit" disabled={pin.length < 4} onClick={verifyPin}
            className="w-full h-12 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-bold disabled:opacity-40">Confirmar</Button>
          <Button variant="ghost" onClick={() => setPinOpen(false)} className="w-full text-slate-500">Cancelar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (step === "welcome") {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-sky-500 to-sky-700 text-white flex flex-col items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="absolute top-6 left-6 flex items-center gap-2 text-white/80">
          <div className="w-10 h-10 grid place-items-center">
            <img data-testid="kiosk-start-logo" src={resolveMedia(company?.logo_url) || "/brand/aipos-logo.png"} alt="" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-lg font-bold font-display">{company?.nombre_comercial || BRAND.name}</span>
        </div>
        <Button
          onClick={attemptExit}
          data-testid="kiosk-exit-button"
          variant="ghost"
          className="absolute top-6 right-6 text-white/70 hover:bg-white/10 text-xs"
        >
          <Lock className="w-3.5 h-3.5 mr-1" /> Salir del kiosko
        </Button>

        <div className="relative text-center px-6 animate-fade-in-up">
          <p className="text-lg text-sky-100/90 mb-4">Bienvenido a</p>
          <h1 className="text-6xl sm:text-7xl font-extrabold font-display tracking-tight mb-6">
            Autoservicio
          </h1>
          <p className="text-xl text-sky-100/90 mb-10 max-w-md mx-auto">
            Elige tus productos, paga con tarjeta y retira tu ticket.
          </p>
          <Button
            data-testid="kiosk-start-button"
            onClick={goFullscreen}
            className="h-20 px-14 rounded-3xl bg-white text-sky-700 hover:bg-sky-50 text-2xl font-bold shadow-2xl"
          >
            Toca aquí para comenzar
          </Button>
          <p className="mt-6 text-sm text-sky-100/70 flex items-center justify-center gap-1.5">
            <Maximize2 className="w-3.5 h-3.5" /> Modo pantalla completa
          </p>
        </div>
      </div>
      {pinDialog}
      </>
    );
  }

  const itemsCount = cart.reduce((s, x) => s + x.cantidad, 0);

  return (
    <div className="h-screen bg-slate-50 flex flex-col select-none overflow-hidden">
      {/* Topbar */}
      <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img data-testid="kiosk-header-logo" src={resolveMedia(company?.logo_url) || "/brand/aipos-logo.png"} alt="" className="w-10 h-10 object-contain drop-shadow-[0_4px_10px_rgba(14,165,233,0.35)]" />
          <div>
            <p className="text-lg font-bold text-slate-900 font-display leading-none">Autoservicio</p>
            <p className="text-xs text-slate-500 mt-0.5">{company?.nombre_comercial}</p>
          </div>
        </div>
        <Button
          data-testid="kiosk-exit-button-top"
          onClick={attemptExit}
          variant="ghost"
          className="h-12 w-12 rounded-2xl text-slate-400 hover:bg-slate-100"
          title="Salir del kiosko"
        >
          <Lock className="w-5 h-5" />
        </Button>
      </div>

      {/* Main split layout */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT: catalog */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Categories */}
          <div className="bg-white border-b border-slate-100 px-5 py-3 flex gap-2 overflow-x-auto shrink-0">
            <button data-testid="kiosk-cat-all" onClick={() => setCat("ALL")}
              className={`px-5 py-2.5 rounded-2xl text-sm font-bold whitespace-nowrap ${cat === "ALL" ? "bg-sky-500 text-white" : "bg-slate-50 text-slate-700 hover:bg-sky-50"}`}>Todos</button>
            {categories.map((c) => (
              <button key={c.category_id} onClick={() => setCat(c.category_id)}
                className={`px-5 py-2.5 rounded-2xl text-sm font-bold whitespace-nowrap ${cat === c.category_id ? "bg-sky-500 text-white" : "bg-slate-50 text-slate-700 hover:bg-sky-50"}`}>{c.nombre}</button>
            ))}
          </div>

          {/* Products grid */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((p) => {
                const inCart = cart.find((x) => x.product_id === p.product_id);
                const noStock = (p.stock || 0) <= 0;
                return (
                  <button
                    key={p.product_id}
                    data-testid={`kiosk-product-${p.product_id}`}
                    onClick={() => add(p)} disabled={noStock}
                    className="bg-white rounded-3xl border border-slate-200 hover:border-sky-300 hover:shadow-lg active:scale-[0.97] transition-all p-3 text-left disabled:opacity-40 relative"
                  >
                    <div className="h-32 rounded-2xl bg-slate-100 overflow-hidden mb-2.5">
                      {p.image_url ? <img src={resolveMedia(p.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="grid place-items-center h-full text-slate-300 text-4xl">{p.nombre.charAt(0)}</div>}
                    </div>
                    <p className="text-sm font-bold text-slate-900 leading-tight line-clamp-2 min-h-[2.5rem]">{p.nombre}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-lg font-extrabold text-sky-700 font-mono-num">{clp(p.precio)}</span>
                      {inCart && (
                        <span className="w-7 h-7 grid place-items-center rounded-full bg-emerald-500 text-white text-xs font-bold font-mono-num shadow">
                          {inCart.cantidad}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT: cart panel */}
        <aside data-testid="kiosk-side-cart" className="w-[340px] xl:w-[380px] shrink-0 bg-white border-l border-slate-200 flex flex-col shadow-[-8px_0_30px_-15px_rgba(15,23,42,0.15)]">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 grid place-items-center">
                <ShoppingCart className="w-5 h-5" />
              </div>
              <div>
                <p className="text-base font-bold text-slate-900 font-display leading-none">Tu orden</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {itemsCount === 0 ? "Vacía" : `${itemsCount} artículo${itemsCount === 1 ? "" : "s"}`}
                </p>
              </div>
            </div>
            {cart.length > 0 && (
              <button
                data-testid="kiosk-clear-cart"
                onClick={() => setCart([])}
                className="text-xs text-slate-400 hover:text-rose-500 font-semibold"
              >
                Vaciar
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {cart.length === 0 ? (
              <div className="text-center py-16 text-slate-300">
                <ShoppingCart className="w-14 h-14 mx-auto mb-3 opacity-40" />
                <p className="text-sm text-slate-400">Toca un producto para agregarlo</p>
              </div>
            ) : cart.map((it) => (
              <div key={it.product_id} data-testid={`kiosk-cart-item-${it.product_id}`} className="bg-slate-50 rounded-2xl p-3">
                <div className="flex justify-between gap-2 items-start">
                  <p className="font-semibold text-sm text-slate-900 leading-tight flex-1 line-clamp-2">{it.nombre}</p>
                  <button onClick={() => rm(it.product_id)} data-testid={`kiosk-cart-rm-${it.product_id}`} className="text-slate-300 hover:text-rose-500 p-1 -mt-0.5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 font-mono-num mt-0.5">{clp(it.precio)} c/u</p>
                <div className="flex items-center justify-between mt-2.5">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => inc(it.product_id, -1)} data-testid={`kiosk-cart-dec-${it.product_id}`} className="w-9 h-9 rounded-xl bg-white border border-slate-200 grid place-items-center text-slate-700 active:scale-95"><Minus className="w-4 h-4" /></button>
                    <span className="w-8 text-center text-base font-bold font-mono-num text-slate-900">{it.cantidad}</span>
                    <button onClick={() => inc(it.product_id, 1)} data-testid={`kiosk-cart-inc-${it.product_id}`} className="w-9 h-9 rounded-xl bg-white border border-slate-200 grid place-items-center text-slate-700 active:scale-95"><Plus className="w-4 h-4" /></button>
                  </div>
                  <span className="font-extrabold font-mono-num text-base text-slate-900">{clp(it.precio * it.cantidad)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Totals + Pay */}
          <div className="border-t border-slate-100 p-4 bg-white shrink-0">
            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-sm text-slate-500">
                <span>Subtotal</span>
                <span className="font-mono-num">{clp(rawSubtotal)}</span>
              </div>
              {promoResult.savings > 0 && (
                <div className="flex justify-between text-sm text-emerald-600 font-semibold">
                  <span>Descuentos</span>
                  <span className="font-mono-num">- {clp(promoResult.savings)}</span>
                </div>
              )}
              <div className="flex justify-between items-end pt-2 border-t border-dashed border-slate-200 mt-1">
                <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Total</span>
                <span data-testid="kiosk-side-total" className="text-2xl font-extrabold text-slate-900 font-mono-num leading-none">{clp(total)}</span>
              </div>
            </div>
            <Button
              data-testid="kiosk-pay-button"
              disabled={cart.length === 0}
              onClick={() => setPayOpen(true)}
              className="w-full h-14 rounded-2xl bg-sky-500 hover:bg-sky-600 disabled:opacity-40 disabled:bg-slate-300 text-white text-base font-bold shadow-lg shadow-sky-500/20"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pagar {clp(total)}
            </Button>
          </div>
        </aside>
      </div>

      {/* Pay screen */}
      <Dialog open={payOpen} onOpenChange={(v) => { setPayOpen(v); if (!v) resetCustomer(); }}>
        <DialogContent className="rounded-3xl max-w-md text-center">
          <DialogHeader><DialogTitle className="text-2xl">Pago con tarjeta</DialogTitle></DialogHeader>
          <div className="py-4">
            <div className="mx-auto w-24 h-24 rounded-3xl bg-sky-100 grid place-items-center animate-soft-pulse">
              <CreditCard className="w-12 h-12 text-sky-600" />
            </div>
            <p className="mt-6 text-slate-600 text-lg" data-testid="kiosk-tbk-estado">{tbkEstado || "Acerca tu tarjeta al terminal"}</p>
            <p className="mt-4 text-4xl font-extrabold text-slate-900 font-mono-num">{clp(total)}</p>
          </div>
          {loyaltyActive && (
            <div data-testid="kiosk-loyalty-section" className="rounded-2xl border border-sky-100 bg-sky-50/70 p-3 space-y-2 text-left">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-sky-600" />
                <p className="text-sm font-bold text-sky-900">¿Acumular puntos? <span className="font-normal text-slate-500">(opcional)</span></p>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Input
                  data-testid="kiosk-loyalty-rut-input"
                  placeholder="RUT (12.345.678-9)"
                  value={custRut}
                  onChange={(e) => setCustRut(formatRut(e.target.value))}
                  className={`h-11 rounded-xl font-mono-num text-base ${custRut && !validateRut(custRut) ? "border-rose-400" : ""}`}
                />
                <Button
                  data-testid="kiosk-loyalty-lookup-btn"
                  type="button"
                  variant="outline"
                  onClick={lookupCustomer}
                  disabled={!custRut || custLoading}
                  className="h-11 rounded-xl px-3"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Input
                data-testid="kiosk-loyalty-name-input"
                placeholder="Nombre"
                value={custName}
                onChange={(e) => setCustName(e.target.value)}
                className="h-11 rounded-xl text-base"
              />
              {custInfo?.found && (
                <div data-testid="kiosk-loyalty-badge" className="rounded-xl bg-white border border-sky-100 px-3 py-2 flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-700">{custInfo.name || custName}</span>
                  <span className="text-xs font-mono-num font-bold text-emerald-600">{clp(custInfo.balance || 0)}</span>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setPayOpen(false)} className="h-14 rounded-2xl text-base">Cancelar</Button>
            <Button data-testid="kiosk-confirm-pay" onClick={pay} className="bg-sky-500 hover:bg-sky-600 text-white h-14 rounded-2xl text-base font-bold">Confirmar pago</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && backToWelcome()}>
        <DialogContent className="rounded-3xl max-w-md text-center">
          <div className="py-6">
            <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center">
              <Check className="w-14 h-14" strokeWidth={3} />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-slate-900 font-display">¡Gracias!</h2>
            {confirm && (
              <>
                <p className="mt-2 text-slate-600 text-lg">Orden <span className="font-mono-num font-bold">#{confirm.numero}</span></p>
                <p className="mt-1 text-slate-500 text-sm">Retira tu ticket a continuación</p>
              </>
            )}
          </div>
          <Button data-testid="kiosk-finish" onClick={backToWelcome} className="h-14 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-base font-bold">Terminar</Button>
        </DialogContent>
      </Dialog>

      {/* PIN exit modal */}
      {pinDialog}
    </div>
  );
}
